/**
 * Tipos de dominio NORMALIZADOS (camelCase) que usa toda la app.
 * Las respuestas crudas de la API (snake_case) se convierten en `shared/domain/adapters.ts`.
 * Este archivo es contrato: cambiarlo implica cambiar docs/CONTRATO_API.md.
 */

/** Fecha calendario sin hora, 'YYYY-MM-DD' (siempre hora de Caracas, UTC-4). */
export type ISODate = string;
/** Instante ISO-8601 con zona, p. ej. '2026-10-17T13:05:00Z'. */
export type ISODateTime = string;

export type Lang = 'es' | 'en';
export type ThemeMode = 'light' | 'dark' | 'system';
export type Moneda = 'USD' | 'VES';

/* ───────────────────────────── Mercado (API existente) ───────────────────────────── */

/** Señales de calidad derivadas de `nota` (OCR, coma corregida, dato de demostración). */
export interface Calidad {
  ocr: boolean;
  /** El pipeline corrigió un valor (p. ej. "coma corregida 15->1.5"): conviene verificarlo. */
  corregido: boolean;
  /** Dato sintético de DEMO (la nota empieza por "[SIMULADO]"). Nunca mezclar con datos reales. */
  simulado: boolean;
  /** Otras notas legibles para el usuario (ya sin el prefijo técnico "OCR:"). */
  avisos: string[];
}

/** Una fila de precio (de /v1/precios/ultimos o /v1/precios/historico), normalizada. */
export interface Precio {
  /** Clave estable = fuente|fecha|rubro|categoria|presentacion|region (igual que la clave única de la BD). */
  id: string;
  rubro: string;
  categoria: string;
  /** en_pie | en_canal | productor | consumidor | fria | caliente | queseras | null (sin dato). */
  presentacion: string | null;
  /** kg | L | unidad | t | carton30 … */
  unidad: string;
  moneda: string;
  /** Precios en `moneda` (USD en todos los datos actuales). */
  min: number;
  max: number;
  prom: number;
  /** Tasa BCV vigente en la fecha del dato y los Bs calculados con ella (null si no hay). */
  tasaBcv: number | null;
  minBs: number | null;
  maxBs: number | null;
  promBs: number | null;
  region: string | null;
  fecha: ISODate;
  /** Fin del período cuando la fuente reporta semana/quincena. */
  fechaFin: ISODate | null;
  fuente: string;
  nota: string | null;
  calidad: Calidad;
}

/** Identifica una "serie": un rubro + categoría + presentación (sin región ni fuente). */
export interface SerieKey {
  rubro: string;
  categoria: string;
  presentacion: string | null;
}

/** Serie con filtros opcionales de región/fuente. */
export interface SerieRef extends SerieKey {
  region?: string | null;
  fuente?: string | null;
}

/** serieId() de shared/domain/series.ts: `${rubro}|${categoria}|${presentacion ?? ''}`. */
export type SerieId = string;

export interface UltimosData {
  tasaBcvActual: number;
  /** Fecha valor de la tasa (puede ser el próximo día hábil, es decir, futura). */
  tasaBcvFecha: ISODate;
  total: number;
  precios: Precio[];
}

export interface TasaBcv {
  fecha: ISODate;
  usd: number;
  eur: number | null;
  fuente: string | null;
}

export interface PuntoHistorico {
  fecha: ISODate;
  fechaFin: ISODate | null;
  min: number;
  max: number;
  prom: number;
  tasaBcv: number | null;
  minBs: number | null;
  maxBs: number | null;
  promBs: number | null;
  region: string | null;
  fuente: string | null;
  nota: string | null;
  calidad: Calidad;
}

export interface Historico {
  rubro: string;
  categoria: string | null;
  presentacion: string | null;
  unidad: string | null;
  /** Orden cronológico ascendente. */
  puntos: PuntoHistorico[];
}

export interface RubroInfo {
  rubro: string;
  categorias: string[];
}

/** Fuente tal como la publica /v1/fuentes (campos tolerantes: la forma real no está documentada). */
export interface Fuente {
  /** Clave que aparece en `precios.fuente` (p. ej. "precioganado"). */
  clave: string;
  nombre: string;
  tipo: string | null;
  url: string | null;
  region: string | null;
  frecuencia: string | null;
  /** convenio | autorizada | pendiente | sin_permiso … (texto libre del catálogo). */
  permiso: string | null;
  ultimoDato: ISODate | null;
}

export type Frescura = 'al_dia' | 'atrasada' | 'desactualizada' | 'sin_datos';

/** Fuente enriquecida (calculada en el cliente o devuelta por /v1/fuentes/detalle). */
export interface FuenteResumen extends Fuente {
  frescura: Frescura;
  diasDesdeUltimo: number | null;
  /** Días esperados entre actualizaciones según `frecuencia`. */
  frecuenciaDias: number;
  filas: number;
  rubros: string[];
  regiones: string[];
  activa: boolean;
}

/* ───────────────────────── Analítica (add-on /v1/mercado/* o cálculo local) ───────────────────────── */

export type Tendencia = 'sube' | 'baja' | 'estable' | 'sin_dato';

export interface Variacion {
  serie: SerieKey;
  region: string | null;
  fuente: string | null;
  unidad: string;
  actual: number;
  fechaActual: ISODate;
  previo: number | null;
  fechaPrevio: ISODate | null;
  difAbs: number | null;
  /** Porcentaje (p. ej. 4.2 = +4,2 %). */
  difPct: number | null;
  tendencia: Tendencia;
  periodoDias: number;
  /** Puntos usados (≥2 para que haya variación). */
  puntos: number;
  /** Valores recientes para sparkline (ascendente en el tiempo). */
  spark: number[];
}

/** Resumen de mercado para la pantalla de inicio (servidor: /v1/mercado/resumen, o calculado). */
export interface ResumenMercado {
  generado: ISODateTime;
  tasaBcvActual: number | null;
  tasaBcvFecha: ISODate | null;
  /** Variación de la tasa BCV en los últimos 7 días (%). */
  tasaBcvVariacionPct7d: number | null;
  cobertura: {
    series: number;
    fuentes: number;
    regiones: number;
    filas: number;
    ultimaActualizacion: ISODate | null;
  };
  suben: Variacion[];
  bajan: Variacion[];
}

export interface FilaComparador {
  region: string | null;
  fuente: string;
  fecha: ISODate;
  min: number;
  max: number;
  prom: number;
  minBs: number | null;
  maxBs: number | null;
  promBs: number | null;
  /** (prom - promedio) / promedio * 100 */
  difVsPromedioPct: number;
  /** 1 = más barato. */
  ranking: number;
}

export interface Comparador {
  serie: SerieKey;
  unidad: string;
  /** Promedio simple de los `prom` de todas las filas. */
  promedio: number;
  filas: FilaComparador[];
  /** Diferencia entre la fila más cara y la más barata, en %. */
  brechaPct: number | null;
}

/* ───────────────────────────── Cuenta ───────────────────────────── */

/** Los 7 perfiles (ids estables, también en la API). `comprador_frigorifico` ya no existe: se lee como `comerciante`. */
export type PerfilTipo =
  | 'ganadero_carne'
  | 'lechero'
  | 'quesero'
  | 'porcicultor'
  | 'bufalero'
  | 'agricultor'
  | 'comerciante';

export type Rol = 'usuario' | 'admin';

export interface Usuario {
  id: string;
  email: string;
  nombre: string | null;
  rol: Rol;
  perfilTipo: PerfilTipo | null;
  regionPrincipal: string | null;
  emailVerificado: boolean;
  creado: ISODateTime;
}

export interface Tokens {
  accessToken: string;
  /** Ausente en modo cookie (el refresh viaja en cookie httpOnly). */
  refreshToken: string | null;
  /** Segundos de vida del access token. */
  expiresIn: number;
}

export interface SesionActiva {
  id: string;
  creada: ISODateTime;
  ultimoUso: ISODateTime;
  userAgent: string | null;
  actual: boolean;
}

/* ───────────────────────────── Preferencias (nube o navegador) ───────────────────────────── */

export type WidgetTipo =
  | 'bcv' // tasa BCV + fecha (la pizarra)
  | 'resumen' // cobertura y última actualización
  | 'serie' // un precio concreto (min/prom/max, $ y Bs, Δ semanal)
  | 'movers' // mayores subidas/bajadas
  | 'favoritos' // favoritos del usuario
  | 'alertas' // alertas activas / últimas disparadas
  | 'calculadora' // calculadora rápida
  | 'comparador' // mini comparador regional
  | 'fuentes' // estado de las fuentes
  | 'tendencias' // sparkline grid
  | 'noticias'; // noticias del campo según los rubros de interés

export type WidgetParams = Record<string, string | number | boolean | null>;

export interface WidgetConfig {
  /** Único dentro de la configuración (uuid corto). */
  id: string;
  tipo: WidgetTipo;
  visible: boolean;
  params: WidgetParams;
}

export interface Favorito {
  id: string;
  rubro: string;
  categoria: string;
  presentacion: string | null;
  region: string | null;
  fuente: string | null;
  creado: ISODateTime;
}

export interface PreferenciasConfig {
  schema: 1;
  /** Perfil con el que se armó la pantalla de inicio (para "restablecer a mi perfil"). */
  perfilAplicado: PerfilTipo | null;
  /** Qué moneda mostrar primero. */
  moneda: Moneda;
  /** 'actual' = Bs a la tasa BCV de hoy; 'fecha_dato' = Bs a la tasa vigente en la fecha del dato. */
  bsModo: 'actual' | 'fecha_dato';
  /** Rendimiento en canal por defecto (% del peso en pie). */
  rendimientoCanalPct: number;
  /** Rendimiento por categoría (p. ej. { toro: 54, cerdo_industrial: 78 }). */
  rendimientoPorCategoria: Record<string, number>;
  home: { widgets: WidgetConfig[] };
  /** Listas blancas de interés; vacío = todo. */
  intereses: { rubros: string[]; regiones: string[]; fuentes: string[] };
  favoritos: Favorito[];
  vista: 'tarjetas' | 'tabla';
  csvSeparador: ',' | ';';
}

/** Envoltorio del servidor (PUT con control de concurrencia optimista). */
export interface PreferenciasServidor {
  version: number;
  config: PreferenciasConfig;
  actualizado: ISODateTime;
}

/* ───────────────────────────── Alertas y notificaciones ───────────────────────────── */

export type AlertaObjetivo = 'precio' | 'bcv';
export type AlertaCondicion = 'sobre' | 'bajo' | 'sube_pct' | 'baja_pct';
export type AlertaMetrica = 'prom' | 'min' | 'max';
export type AlertaCanal = 'app' | 'email' | 'push';
export type AlertaCooldown = 'una_vez' | 'cada_dato' | 'diaria';

export interface AlertaFiltro {
  rubro: string;
  categoria: string;
  presentacion: string | null;
  region: string | null;
  fuente: string | null;
}

export interface Alerta {
  id: string;
  /** 'local' = guardada en el navegador (sin cuenta); 'servidor' = en la cuenta. */
  origen: 'local' | 'servidor';
  nombre: string | null;
  activa: boolean;
  objetivo: AlertaObjetivo;
  /** null cuando objetivo = 'bcv'. */
  filtro: AlertaFiltro | null;
  metrica: AlertaMetrica;
  moneda: Moneda;
  condicion: AlertaCondicion;
  /** Precio (sobre/bajo) o porcentaje (sube_pct/baja_pct). */
  umbral: number;
  /** Ventana para sube_pct/baja_pct. */
  periodoDias: number;
  canales: AlertaCanal[];
  cooldown: AlertaCooldown;
  estado: 'armada' | 'disparada' | 'pausada';
  creada: ISODateTime;
  ultimaEvaluacion: ISODateTime | null;
  ultimaDisparada: ISODateTime | null;
  ultimoValor: number | null;
}

export type AlertaInput = Omit<
  Alerta,
  'id' | 'origen' | 'estado' | 'creada' | 'ultimaEvaluacion' | 'ultimaDisparada' | 'ultimoValor'
>;

export interface Notificacion {
  id: string;
  origen: 'local' | 'servidor';
  tipo: 'alerta' | 'sistema' | 'resumen';
  titulo: string;
  cuerpo: string;
  datos: Record<string, unknown>;
  /** Ruta interna de la app a abrir (p. ej. "/precios?rubro=bovino"). */
  enlace: string | null;
  creada: ISODateTime;
  leida: boolean;
}

/* ───────────────────────────── Capacidades del backend ───────────────────────────── */

export interface Capacidades {
  /** false = el backend no tiene el add-on montado (modo básico: solo lectura + datos locales). */
  disponible: boolean;
  version: string;
  auth: {
    habilitada: boolean;
    registroAbierto: boolean;
    cookieMode: boolean;
    verificacionEmail: boolean;
    recuperacionPassword: boolean;
  };
  push: { habilitado: boolean; vapidPublicKey: string | null };
  email: { habilitado: boolean };
  mercado: { variaciones: boolean; resumen: boolean; fuentesDetalle: boolean };
  admin: { fuentes: boolean };
  limites: { maxAlertas: number; maxFavoritos: number; maxSesiones: number };
}

/** Capacidades del "modo básico" (API sin add-on o sin respuesta). */
export const CAPACIDADES_BASICAS: Capacidades = {
  disponible: false,
  version: '0',
  auth: {
    habilitada: false,
    registroAbierto: false,
    cookieMode: false,
    verificacionEmail: false,
    recuperacionPassword: false,
  },
  push: { habilitado: false, vapidPublicKey: null },
  email: { habilitado: false },
  mercado: { variaciones: false, resumen: false, fuentesDetalle: false },
  admin: { fuentes: false },
  limites: { maxAlertas: 20, maxFavoritos: 100, maxSesiones: 10 },
};

/* ───────────────────────── Añadidos por FE-DATA (solo se agregan tipos; ninguno existente cambia) ───────────────────────── */

/** Fuente con su nota pública (respuesta de /v1/fuentes/detalle). */
export interface FuenteDetalle extends FuenteResumen {
  notasPublicas: string | null;
}

/** Variaciones de mercado para un período, vengan del servidor o calculadas en el cliente. */
export interface VariacionesResultado {
  periodoDias: number;
  generado: ISODateTime | null;
  origen: 'servidor' | 'cliente';
  variaciones: Variacion[];
}

/** Preferencias tal como las guarda el servidor (`config: null` = nunca se guardaron; `version: 0`). */
export interface PreferenciasRemotas {
  version: number;
  config: PreferenciasConfig | null;
  actualizado: ISODateTime | null;
}

/** Respuesta de login, registro y refresh. */
export interface SesionAuth {
  usuario: Usuario;
  tokens: Tokens;
}
